// Awards (Phase 5) — role-adaptive. Everyone nominates peers for an award
// (routed through the Approvals inbox: nominee's manager → HR; on approval a
// recognition with reward points is granted). HR also sees every award.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Trophy } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const tone = (s) => (s === 'Awarded' ? 'success' : s === 'Rejected' ? 'error' : 'warning');

function AwardCard({ a, showNominee }) {
  return (
    <Card style={{ padding: 16 }}>
      <div className="flex items-start justify-between" style={{ gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600, color: AG.text }}>
            {showNominee ? `${a.nomineeName} · ` : ''}{a.category}{a.points ? ` · ${a.points} pts` : ''}
          </div>
          <div style={{ fontSize: 13, color: AG.n600, marginTop: 3 }}>“{a.citation}”</div>
          <div style={{ fontSize: 12, color: AG.n500, marginTop: 4 }}>Nominated by {a.nominatorName}</div>
        </div>
        <Pill tone={tone(a.status)} dot>{a.status}</Pill>
      </div>
    </Card>
  );
}

function NominateDialog({ open, onClose, onDone, showToast }) {
  const [dir, setDir] = useState([]);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ nomineeId: '', category: 'Spot Award', citation: '', points: '50' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.awardDirectory().then(setDir).catch(() => setDir([]));
    api.awardCategories().then((c) => { setCats(c); setF((s) => ({ ...s, category: c[0] || 'Spot Award' })); }).catch(() => setCats([]));
  }, [open]);
  const submit = async () => {
    if (!f.nomineeId || !f.citation.trim() || busy) return; setBusy(true);
    try { await api.nominateAward({ ...f, points: Number(f.points) || 0 }); showToast?.('Nomination submitted for approval'); onClose(); onDone?.(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Nominate for an award</DialogTitle>
      <DialogContent>
        <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Colleague</label>
            <select className="ag-input" value={f.nomineeId} onChange={(e) => setF({ ...f, nomineeId: e.target.value })} style={{ marginTop: 4 }}>
              <option value="">Select…</option>
              {dir.map((d) => <option key={d.id} value={d.id}>{d.name}{d.dept ? ` · ${d.dept}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Award</label>
            <select className="ag-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} style={{ marginTop: 4 }}>
              {cats.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <TextField size="small" label="Citation" multiline minRows={2} value={f.citation} onChange={(e) => setF({ ...f, citation: e.target.value })} placeholder="Why do they deserve this award?" />
          <TextField size="small" type="number" label="Reward points" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} inputProps={{ min: 0, max: 1000 }} />
        </div>
      </DialogContent>
      <DialogActions><OutlineBtn onClick={onClose} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={submit} disabled={busy || !f.nomineeId || !f.citation.trim()}>Nominate</PrimaryBtn></DialogActions>
    </Dialog>
  );
}

export default function Awards({ showToast }) {
  const { user } = useAuth();
  const isHR = user?.role === 'HR';
  const [mine, setMine] = useState(null);
  const [all, setAll] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => {
    api.myAwards().then(setMine).catch(() => setMine({ received: [], nominated: [] }));
    if (isHR) api.awardsAll().then(setAll).catch(() => setAll([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!mine) return <CenterSpinner />;

  return (
    <div className="space-y-5 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Awards" sub="Nominate colleagues for recognition" />
        <PrimaryBtn onClick={() => setOpen(true)}><Plus size={14} style={{ marginRight: 6 }} /> Nominate</PrimaryBtn>
      </div>

      <div>
        <div style={{ fontWeight: 600, color: AG.text, marginBottom: 8 }}>Awards I've received</div>
        {mine.received.length === 0 ? <Card><EmptyState icon={Trophy} title="No awards yet" desc="Recognitions you're awarded will appear here." /></Card>
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{mine.received.map((a) => <AwardCard key={a.id} a={a} />)}</div>}
      </div>

      <div>
        <div style={{ fontWeight: 600, color: AG.text, marginBottom: 8 }}>My nominations</div>
        {mine.nominated.length === 0 ? <Card><EmptyState icon={Trophy} title="No nominations" desc="Nominate a colleague to get started." /></Card>
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{mine.nominated.map((a) => <AwardCard key={a.id} a={a} showNominee />)}</div>}
      </div>

      {isHR && (
        <div>
          <div style={{ fontWeight: 600, color: AG.text, marginBottom: 8 }}>All awards</div>
          {!all ? <CenterSpinner /> : all.length === 0 ? <Card><EmptyState icon={Trophy} title="No awards" desc="Nominations across the organization appear here." /></Card>
            : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{all.map((a) => <AwardCard key={a.id} a={a} showNominee />)}</div>}
        </div>
      )}

      <NominateDialog open={open} onClose={() => setOpen(false)} onDone={load} showToast={showToast} />
    </div>
  );
}
